import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { useSuperAdminOrg } from "@/shared/contexts/SuperAdminOrgContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Loader2 } from "lucide-react";
import { useToast } from "@/shared/hooks/use-toast";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import { OrganizationService } from "@/features/Organization/services/organizationService";
import { IntegrationBaseService } from "@/features/Integrations/services/integrationsBaseService";
import { type ToolOption } from "@/shared/components/ui/tool-select";
import { getIntegrationDescription } from "@/shared/utils/i18nHelper";
import {
  ModelPurposeAccordion,
  type PurposeLabels,
} from "@/shared/components/ai/ModelPurposeAccordion";
import {
  aprovado,
  nivelVazio,
  type FieldCheck,
  type NivelDaCadeia,
  type PurposeChoice,
} from "@/shared/types/aiModels";
import { FallbackChain } from "@/shared/components/ai/FallbackChain";
import { Button } from "@/shared/components/ui/button";
import { readModelCatalog } from "@/shared/utils/modelCatalog";

/**
 * As finalidades que a ORGANIZAÇÃO escolhe. A transcrição é da plataforma — ela não tem
 * bloco aqui, e é por isso que a lista é desta tela e não do componente.
 */
const FINALIDADES = ["conversational", "embedding"] as const;
type FinalidadeOrg = (typeof FINALIDADES)[number];

export const ModelsSettings = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const { selectedOrganization } = useSuperAdminOrg();
  const queryClient = useQueryClient();

  const activeOrgId = selectedOrganization?.id;
  const effectiveOrgId = activeOrgId ?? user?.organizationId;

  /**
   * Uma integração por propósito (AB#85432).
   *
   * Antes era uma só, e a organização que conversava por um provedor era obrigada a
   * indexar por ele — o que travava justamente quem usa um provedor que não serve
   * embedding. São duas escolhas independentes, e cada uma carrega o próprio catálogo.
   */
  const [convIntegrationId, setConvIntegrationId] = useState<string | null>(null);
  const [initialConvIntegrationId, setInitialConvIntegrationId] = useState<string | null>(null);
  const [embIntegrationId, setEmbIntegrationId] = useState<string | null>(null);
  const [initialEmbIntegrationId, setInitialEmbIntegrationId] = useState<string | null>(null);
  const [conversationalModels, setConversationalModels] = useState<string[]>([]);
  const [embeddingModels, setEmbeddingModels] = useState<string[]>([]);
  const [completion, setCompletion] = useState<string>("");
  const [initialCompletion, setInitialCompletion] = useState<string>("");
  const [embedding, setEmbedding] = useState<string>("");
  const [initialEmbedding, setInitialEmbedding] = useState<string>("");
  /**
   * Resultado da validacao de UM campo.
   *
   * `unreachable` e `unavailable` existem separados de `done` porque nao dizem nada sobre
   * o modelo: a chamada nao aconteceu, ou o relatorio veio sem gateway configurado.
   * Trata-los como reprovacao acusaria o cadastro por uma falha de rede.
   */
  const [checks, setChecks] = useState<{ conversational: FieldCheck; embedding: FieldCheck }>({
    conversational: { state: "idle" },
    embedding: { state: "idle" },
  });
  /**
   * Seções abertas do acordeon (AB#85617).
   *
   * A tela era uma lista corrida de campos; virou acordeon para ficar igual à de superadmin,
   * e porque com o bloco de contingência abaixo a lista dobraria de tamanho.
   */
  const [secoesAbertas, setSecoesAbertas] = useState<string[]>([]);

  /**
   * O bloco de CONTINGÊNCIA (AB#85617): mesmo formato, estado próprio, salvar próprio.
   *
   * Estado separado do principal porque os dois gravam por rotas diferentes — misturá-los
   * faria um salvamento arrastar o outro.
   */
  /**
   * Os NÍVEIS da cadeia de contingência, na ordem de tentativa (AB#85617).
   *
   * Lista, e não um elo só: o dado e o gateway sempre suportaram a cadeia inteira. Cada
   * nível traz as duas finalidades; deixar uma em branco simplesmente não acrescenta elo à
   * cadeia dela, que é como os comprimentos voltam a ser independentes na gravação.
   */
  const [niveisContingencia, setNiveisContingencia] = useState<
    Array<NivelDaCadeia<FinalidadeOrg>>
  >([]);
  const [niveisInicial, setNiveisInicial] = useState<Array<NivelDaCadeia<FinalidadeOrg>>>([]);
  const [contingenciaPendente, setContingenciaPendente] = useState(false);

  const { data: escolha, isLoading: isLoadingOrg } = useQuery({
    queryKey: ['organization-ai-models', effectiveOrgId],
    queryFn: async () => {
      if (!effectiveOrgId) return null;
      return await OrganizationService.getAiModels(effectiveOrgId);
    },
    enabled: !!effectiveOrgId,
  });

  useEffect(() => {
    if (!escolha) return;
    const conv = escolha.conversational;
    const emb = escolha.embedding;
    setConvIntegrationId(conv?.integration_base_id ?? null);
    setInitialConvIntegrationId(conv?.integration_base_id ?? null);
    setCompletion(conv?.model ?? "");
    setInitialCompletion(conv?.model ?? "");
    // Sem escolha de embedding, o campo abre na integração do conversacional: é o caso
    // comum, e deixá-lo vazio esconderia que há um catálogo a escolher.
    setEmbIntegrationId(emb?.integration_base_id ?? conv?.integration_base_id ?? null);
    setInitialEmbIntegrationId(emb?.integration_base_id ?? null);
    setEmbedding(emb?.model ?? "");
    setInitialEmbedding(emb?.model ?? "");
    // Abre o que falta configurar: finalidade com modelo escolhido já diz o que precisa no
    // cabeçalho fechado. Mesmo critério da tela da plataforma.
    setSecoesAbertas(
      FINALIDADES.filter((purpose) =>
        purpose === "conversational" ? !conv?.model : !emb?.model
      )
    );

    // A cadeia de contingência vem no mesmo payload, num ramo à parte (AB#85617).
    //
    // O dado é uma cadeia POR FINALIDADE, de comprimentos independentes; a tela mostra
    // NÍVEIS. O nível N reúne o N-ésimo elo de cada finalidade, e o número de níveis é o da
    // cadeia mais longa — finalidade mais curta fica em branco nos níveis excedentes.
    const cadeias = escolha.fallbacks ?? { conversational: [], embedding: [] };
    const profundidade = Math.max(
      cadeias.conversational?.length ?? 0,
      cadeias.embedding?.length ?? 0
    );
    const lidos = Array.from({ length: profundidade }, (_, nivel) =>
      nivelVazio(FINALIDADES, {
        conversational: {
          integrationId: cadeias.conversational?.[nivel]?.integration_base_id ?? null,
          model: cadeias.conversational?.[nivel]?.model ?? "",
        },
        embedding: {
          integrationId: cadeias.embedding?.[nivel]?.integration_base_id ?? null,
          model: cadeias.embedding?.[nivel]?.model ?? "",
        },
      } as Record<FinalidadeOrg, PurposeChoice>)
    );

    setNiveisContingencia(lidos);
    setNiveisInicial(lidos);
  }, [escolha]);

  const { data: providers, isLoading: isLoadingProviders } = useQuery<ToolOption[]>({
    queryKey: ['integration-base-models', effectiveOrgId, i18n.language],
    queryFn: async (): Promise<ToolOption[]> => {
      if (!effectiveOrgId) return [];
      const integrationsBases = await IntegrationBaseService.getAll({
        classification: 'llm',
        status: 'active'
      });
      return integrationsBases.map((integration): ToolOption => ({
        id: integration.id,
        name: integration.label,
        description: getIntegrationDescription(integration, i18n.language) || '',
        logo_url: integration.logo_url || integration.template?.logo_url || null
      }));
    },
    enabled: !!effectiveOrgId,
  });

  /**
   * Catalogo declarado por UMA integracao, ja separado pelo que cada modelo serve
   * (`@/shared/utils/modelCatalog`, compartilhado com a tela da plataforma).
   */
  const carregarCatalogo = async (integrationId: string) => {
    const integration = await IntegrationBaseService.getById(integrationId);
    return readModelCatalog(integration?.metadata);
  };

  // Um catalogo por proposito, cada um da SUA integracao. A guarda de cancelamento existe
  // porque trocar de provedor dispara um fetch novo sem cancelar o anterior, e a resposta
  // que chega por ultimo vence — a tela mostraria os modelos de uma integracao com outra
  // selecionada.
  useEffect(() => {
    let cancelado = false;
    if (!convIntegrationId) {
      setConversationalModels([]);
      return;
    }
    carregarCatalogo(convIntegrationId)
      .then(({ conversational }) => {
        if (cancelado) return;
        setConversationalModels(conversational);
        // Modelo que a nova integração não declara não pode continuar selecionado: ele
        // seria chamado com a credencial dela e falharia. Sem fallback para `settings` —
        // a integração descreve o provedor, não a escolha da organização.
        setCompletion((atual) => (atual && conversational.includes(atual) ? atual : ""));
      })
      .catch((error) => {
        if (cancelado) return;
        // Catálogo que não carrega deixa a finalidade sem opções; a falha vai para a tela
        // em vez do console, que ninguém abre.
        setConversationalModels([]);
        toast({
          variant: "destructive",
          title: t("common.error"),
          description: resolveApiErrorMessage(error, t),
        });
      });
    return () => {
      cancelado = true;
    };
  }, [convIntegrationId, toast, t]);

  useEffect(() => {
    let cancelado = false;
    if (!embIntegrationId) {
      setEmbeddingModels([]);
      return;
    }
    carregarCatalogo(embIntegrationId)
      .then(({ embedding: embeddings }) => {
        if (cancelado) return;
        setEmbeddingModels(embeddings);
        setEmbedding((atual) => (atual && embeddings.includes(atual) ? atual : ""));
      })
      .catch((error) => {
        if (cancelado) return;
        setEmbeddingModels([]);
        toast({
          variant: "destructive",
          title: t("common.error"),
          description: resolveApiErrorMessage(error, t),
        });
      });
    return () => {
      cancelado = true;
    };
  }, [embIntegrationId, toast, t]);

  /**
   * Valida UM proposito, com a escolha que esta na TELA (AB#85434).
   *
   * Antes isto rodava depois do save, e o relatorio chegava com a configuracao errada ja
   * gravada — a organizacao ficava sem LLM ate alguem reabrir a tela. Validando antes, o
   * Save so libera com os dois campos aprovados.
   *
   * Manda so o proposito que mudou: assim trocar um campo nao gasta uma chamada ao provedor
   * com o outro, que nao mudou.
   */
  const checarModelo = useCallback(
    async (
      purpose: "conversational" | "embedding",
      integrationId: string | null,
      model: string,
      cancelado: () => boolean
    ) => {
      if (!effectiveOrgId || !integrationId || !model) {
        setChecks((atual) => ({ ...atual, [purpose]: { state: "idle" } }));
        return;
      }

      setChecks((atual) => ({ ...atual, [purpose]: { state: "checking" } }));
      const aplicar = (check: FieldCheck) => {
        if (cancelado()) return;
        setChecks((atual) => ({ ...atual, [purpose]: check }));
      };

      try {
        const report = await OrganizationService.testAiModels(effectiveOrgId, {
          purpose,
          integration_base_id: integrationId,
          model,
        });
        aplicar(
          report.status === "ok"
            ? { state: "done", outcome: report[purpose] }
            : { state: "unavailable", reason: report.status }
        );
      } catch {
        aplicar({ state: "unreachable" });
      }
    },
    [effectiveOrgId]
  );

  // Um efeito por campo: trocar o conversacional nao revalida o embedding. Trocar de
  // PROVEDOR revalida os dois, e deve mesmo — os dois modelos passam a ser de outra
  // integracao, com outra credencial e outro endpoint.
  //
  // A guarda de cancelamento existe porque a resposta mais lenta de uma escolha anterior
  // sobrescreveria a atual, aprovando um modelo que ja nao esta selecionado.
  useEffect(() => {
    let cancelado = false;
    void checarModelo("conversational", convIntegrationId, completion, () => cancelado);
    return () => {
      cancelado = true;
    };
  }, [checarModelo, convIntegrationId, completion]);

  useEffect(() => {
    let cancelado = false;
    void checarModelo("embedding", embIntegrationId, embedding, () => cancelado);
    return () => {
      cancelado = true;
    };
  }, [checarModelo, embIntegrationId, embedding]);

  const save = useMutation({
    mutationFn: async () => {
      if (!effectiveOrgId) throw new Error(t('common.errors.organizationNotFound'));

      if (!convIntegrationId) throw new Error(t('common.errors.organizationNotFound'));

      // Uma chamada só. Antes eram duas — organização e integração —, e a escolha
      // ficava pela metade quando a segunda falhava. O backend grava as duas linhas e as
      // chaves de compatibilidade na mesma transação (AB#85434).
      await OrganizationService.updateAiModels(effectiveOrgId, {
        conversational_integration_base_id: convIntegrationId,
        conversational_model: completion,
        embedding_integration_base_id: embIntegrationId,
        embedding_model: embedding,
      });
    },
    onSuccess: () => {
      setInitialConvIntegrationId(convIntegrationId);
      setInitialEmbIntegrationId(embIntegrationId);
      setInitialCompletion(completion);
      setInitialEmbedding(embedding);
      toast({
        title: t("settings.models.updateSuccess"),
        description: t("settings.models.updateSuccessDescription"),
      });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      queryClient.invalidateQueries({ queryKey: ['organization-ai-models'] });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: t("common.error"),
        // A mensagem do backend, quando ele diz POR QUE recusou — uma escolha de modelo
        // que a base nao suporta, por exemplo. O texto generico deixava o usuario sem
        // saber que o problema era o modelo que ele acabou de escolher (AB#85518).
        description: resolveApiErrorMessage(error, t),
      });
    }
  });

  const salvarContingencia = useMutation({
    mutationFn: async () => {
      if (!effectiveOrgId) throw new Error(t("common.errors.organizationNotFound"));

      // Os NÍVEIS da tela viram a cadeia de CADA finalidade (AB#85617). Finalidade em
      // branco num nível simplesmente não entra na cadeia dela — é assim que as duas
      // cadeias voltam a ter comprimentos independentes.
      const cadeiaDe = (purpose: FinalidadeOrg) =>
        niveisContingencia
          .map((nivel) => nivel.escolhas[purpose])
          .filter((elo) => elo.integrationId && elo.model)
          .map((elo) => ({ integration_base_id: elo.integrationId!, model: elo.model }));

      await OrganizationService.updateAiModelsFallback(effectiveOrgId, {
        conversational: cadeiaDe("conversational"),
        embedding: cadeiaDe("embedding"),
      });
    },
    onSuccess: () => {
      setNiveisInicial(niveisContingencia);
      toast({
        title: t("settings.organization.fallback.updateSuccess"),
        description: t("settings.organization.fallback.updateSuccessDescription"),
      });
      queryClient.invalidateQueries({ queryKey: ["organization-ai-models"] });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: resolveApiErrorMessage(error, t),
      });
    },
  });

  /**
   * A cadeia mudou em relação ao que está gravado.
   *
   * Compara por serialização porque a comparação é estrutural e a lista é curta — no
   * máximo três níveis de dois campos. Um comparador campo a campo aqui só repetiria isso
   * com mais linhas e mais chance de esquecer um campo novo.
   */
  const contingenciaMudou =
    JSON.stringify(niveisContingencia) !== JSON.stringify(niveisInicial);

  /**
   * Há finalidade escolhida cujo modelo ainda não passou na sonda.
   *
   * Finalidade em branco não é pendência: é escolha legítima de quem não usa aquele
   * propósito. Mesmo critério da tela da plataforma.
   */
  const pendente = FINALIDADES.some((purpose) => {
    const escolhido =
      purpose === "conversational"
        ? { integrationId: convIntegrationId, model: completion }
        : { integrationId: embIntegrationId, model: embedding };
    if (!escolhido.integrationId || !escolhido.model) return false;
    return !aprovado(checks[purpose]);
  });

  const hasChanges =
    convIntegrationId !== initialConvIntegrationId ||
    embIntegrationId !== initialEmbIntegrationId ||
    completion !== initialCompletion ||
    embedding !== initialEmbedding;

  return (
    <>
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{t("settings.models.title")}</CardTitle>
        <CardDescription>{t("settings.models.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoadingOrg ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <ModelPurposeAccordion
            purposes={FINALIDADES}
            labels={
              {
                conversational: {
                  section: t("settings.organization.conversationalProvider"),
                  help: t("settings.organization.defaultModelNameHelp"),
                  provider: t("settings.organization.conversationalProvider"),
                  model: t("settings.organization.defaultModelName"),
                },
                embedding: {
                  section: t("settings.organization.embeddingProvider"),
                  help: t("settings.organization.defaultEmbeddingModelHelp"),
                  provider: t("settings.organization.embeddingProvider"),
                  model: t("settings.organization.defaultEmbeddingModel"),
                },
              } satisfies Record<FinalidadeOrg, PurposeLabels>
            }
            values={{
              conversational: { integrationId: convIntegrationId, model: completion },
              embedding: { integrationId: embIntegrationId, model: embedding },
            }}
            checks={checks}
            catalogs={{ conversational: conversationalModels, embedding: embeddingModels }}
            providers={providers || []}
            loadingProviders={isLoadingProviders}
            open={secoesAbertas}
            onOpenChange={setSecoesAbertas}
            onChange={(purpose: FinalidadeOrg, patch: Partial<PurposeChoice>) => {
              // O estado desta tela é por campo, não um mapa por finalidade: o adaptador
              // mora aqui, e não no componente, que atende as duas telas.
              if (purpose === "conversational") {
                if (patch.integrationId !== undefined) setConvIntegrationId(patch.integrationId);
                if (patch.model !== undefined) setCompletion(patch.model);
                return;
              }
              if (patch.integrationId !== undefined) setEmbIntegrationId(patch.integrationId);
              if (patch.model !== undefined) setEmbedding(patch.model);
            }}
            notConfiguredLabel={t("settings.organization.notConfigured")}
            selectModelPlaceholder={t("settings.organization.defaultModelNamePlaceholder")}
            saving={save.isPending}
            /* Mesma regra da tela de superadmin (AB#85617): bloqueia a finalidade que está
               CONFIGURADA e ainda não aprovada. Exigir veredito das duas sempre travava o
               salvamento de quem não usa embedding — estado legítimo, e a sonda o reporta
               como `skipped`, que nunca é "aprovado". Gravar um modelo que não responde
               segue barrado, que é o que o AB#85434 veio garantir. */
            canSave={hasChanges && !pendente}
            saveLabel={t("common.actions.save")}
            savingLabel={t("common.actions.saving")}
            onSubmit={() => save.mutate()}
          />
        )}
      </CardContent>
    </Card>

    {/* O bloco de CONTINGÊNCIA (AB#85617): irmão do de cima, mesmo formato, salvar próprio.
        Quando o principal falha, o gateway repete nele e só então passa para cá. */}
    <Card>
      <CardHeader>
        <CardTitle>{t("settings.organization.fallback.title")}</CardTitle>
        <CardDescription>{t("settings.organization.fallback.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoadingOrg ? null : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              salvarContingencia.mutate();
            }}
            className="space-y-4"
          >
            <FallbackChain
              purposes={FINALIDADES}
              labels={
                {
                  conversational: {
                    section: t("settings.organization.conversationalProvider"),
                    help: t("settings.organization.defaultModelNameHelp"),
                    provider: t("settings.organization.conversationalProvider"),
                    model: t("settings.organization.defaultModelName"),
                  },
                  embedding: {
                    section: t("settings.organization.embeddingProvider"),
                    help: t("settings.organization.defaultEmbeddingModelHelp"),
                    provider: t("settings.organization.embeddingProvider"),
                    model: t("settings.organization.defaultEmbeddingModel"),
                  },
                } satisfies Record<FinalidadeOrg, PurposeLabels>
              }
              niveis={niveisContingencia}
              onNiveisChange={setNiveisContingencia}
              onPendingChange={setContingenciaPendente}
              providers={providers || []}
              loadingProviders={isLoadingProviders}
              notConfiguredLabel={t("settings.organization.fallback.notConfigured")}
              selectModelPlaceholder={t("settings.organization.defaultModelNamePlaceholder")}
              loadCatalog={async (purpose: FinalidadeOrg, integrationId: string) =>
                (await carregarCatalogo(integrationId))[purpose]
              }
              testModel={async (
                purpose: FinalidadeOrg,
                integrationId: string,
                model: string
              ): Promise<FieldCheck> => {
                if (!effectiveOrgId) return { state: "idle" };
                try {
                  const report = await OrganizationService.testAiModels(effectiveOrgId, {
                    purpose,
                    integration_base_id: integrationId,
                    model,
                  });
                  return report.status === "ok"
                    ? { state: "done", outcome: report[purpose] }
                    : { state: "unavailable", reason: report.status };
                } catch {
                  return { state: "unreachable" };
                }
              }}
              texts={{
                linkTitle: (posicao) =>
                  t("settings.fallbackChain.linkTitle", { position: posicao }),
                addLink: t("settings.fallbackChain.addLink"),
                removeLink: t("settings.fallbackChain.removeLink"),
                hint: t("settings.fallbackChain.hint"),
                empty: t("settings.fallbackChain.empty"),
              }}
            />

            <Button
              type="submit"
              disabled={
                salvarContingencia.isPending || !contingenciaMudou || contingenciaPendente
              }
              className="w-full sm:w-auto"
            >
              {salvarContingencia.isPending
                ? t("common.actions.saving")
                : t("common.actions.save")}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
    </>
  );
};
